import { Component, useCallback, useEffect } from 'react';
import type { Column, RowRenderProps, SortingRule } from 'react-table';

import { TEAM_GROUPS } from '../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { window } from '../../../../globals';
import { isFeatureEnabled } from '../../../../helpers/features';
import { totalProjectCount } from '../../../../helpers/teams';
import { ErrorAlert } from '../../../generic';
import AccessGate from '../../../generic/Gates/AccessGate';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import { teamFeature } from '../../billing/features/consts';
import Tabs from '../../../generic/Tabs';
import TeamGroupsTable from './teamGroups/TeamGroupsTable';
import { CreateTeamModal, EditTeamModal } from './CreateEditTeamModal';
import CreateTeamGroupModal from './teamGroups/CreateTeamGroupModal';
import TeamsTableRowActions from './TeamsTableRowActions';
import { useTeams } from '../../../../hooks/api';
import type { UpdateTeamRequest, TeamWithAssociations } from '../../../../../shared/types/routes/teams';
import { useQuery } from '../../../../contexts/RouterContext';

type Props = {
  teams: TeamWithAssociations[];
  isFetchingTeams: boolean;
  fetchTeamsError: Error | null;
  isTeamGroupsEnabled: boolean;
  activeTab?: 'teams' | 'teamGroups';
  setActiveTab: (tab: 'teams' | 'teamGroups') => void;
};

type State = {
  sortMode: SortingRule;
  searchValue: string;
  //  We display the edit modal if teamToEdit is not null.
  teamToEdit: UpdateTeamRequest | null;
  showCreateTeamModal: boolean;
  showCreateTeamGroupModal: boolean;
};

class OrganizationTeamsIndex extends Component<Props, State> {
  readonly state: State = {
    sortMode: {
      id: 'name',
      desc: false,
    },
    searchValue: '',
    teamToEdit: null,
    showCreateTeamModal: false,
    showCreateTeamGroupModal: false,
  };

  onShowCreateTeamModal = () => this.setState({ showCreateTeamModal: true });

  onHideCreateTeamModal = () => this.setState({ showCreateTeamModal: false });

  onShowCreateTeamGroupModal = () => this.setState({ showCreateTeamGroupModal: true });

  onHideCreateTeamGroupModal = () => this.setState({ showCreateTeamGroupModal: false });

  onShowEditTeamModal = (teamId: number) => {
    const teamToEdit = this.getTeamByTeamId(teamId);
    if (!teamToEdit) {
      return;
    }
    this.setState({ teamToEdit: (teamToEdit as unknown as UpdateTeamRequest) });
  };

  onHideEditTeamModal = () => this.setState({ teamToEdit: null });

  onSortedChanged = (sortMode: SortingRule) => this.setState({ sortMode });

  getTeamByTeamId = (teamId: number): TeamWithAssociations | null =>
    this.props.teams.find((team) => team.id === teamId) ?? null;

  getColumns = (): Array<Column<any>> => {
    const { sortMode } = this.state;

    return [
      {
        id: 'name',
        Header: <TableHeader title="Team Name" sort={{ id: 'name', currentMode: sortMode }} />,
        accessor: 'name',
        resizable: false,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'memberCount',
        Header: <TableHeader title="Members" sort={{ id: 'memberCount', currentMode: sortMode }} />,
        accessor: ({ teamUsers }: TeamWithAssociations) => teamUsers?.length || 0,
        width: 250,
        resizable: false,
      },
      {
        id: 'projectCount',
        accessor: totalProjectCount,
        Header: <TableHeader title="Projects" sort={{ id: 'projectCount', currentMode: sortMode }} />,
        // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
        Cell: ({ original }: { original: TeamWithAssociations } & RowRenderProps) => (
          <>
            {totalProjectCount(original)}
            <TeamsTableRowActions
              teamId={original.id}
              teamName={original.name}
              onEditTeam={this.onShowEditTeamModal}
            />
          </>
        ),
        width: 250,
        resizable: false,
        style: { overflow: 'visible', paddingRight: '15px' },
      },
    ];
  };

  get filteredTeams(): TeamWithAssociations[] {
    const { teams } = this.props;
    const { searchValue } = this.state;

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      return teams.filter((team: TeamWithAssociations) => team.name.toLowerCase().includes(searchLower));
    }

    return teams;
  }

  render() {
    const { isTeamGroupsEnabled, isFetchingTeams, fetchTeamsError, activeTab, setActiveTab } = this.props;
    const { teamToEdit: activeTeam, searchValue } = this.state;
    const filteredTeams = this.filteredTeams;
    //  This check also applies to TeamGroups, since they are ultimately just a collection of teams.
    const canCreateTeams = window.hasPermission({ resourceType: ResourceType.Team, action: Action.Create });

    return (
      <AccessGate level="premium" behavior="showPage" focusFeature={teamFeature}>
        {fetchTeamsError && !activeTeam && (
        // Show the error alert on the page itself only when there isn't an open modal.
          <ErrorAlert status={{ errors: [fetchTeamsError] }} />
        )}
        <Header heading="Teams">
          <Header.Paragraph>
            Create teams and assign users and projects to them.
            {' '}
            Users without org-level permissions can only access data associated with their teams.
          </Header.Paragraph>
        </Header>
        {isTeamGroupsEnabled && (
          <Tabs>
            <Tabs.Tab
              label="Teams"
              //  This handles the case where the tab is not set.
              isActive={!activeTab || activeTab === 'teams'}
              onSelect={() => setActiveTab('teams')}
            />
            <Tabs.Tab
              label="Team Groups"
              isActive={activeTab === 'teamGroups'}
              onSelect={() => setActiveTab('teamGroups')}
            />
          </Tabs>
        )}
        <SettingsPageTableActions>
          <SettingsPageTableActions.TooltipButton
            id="add-team-button"
            size="large"
            type="secondary"
            onClick={activeTab === 'teams' ? this.onShowCreateTeamModal : this.onShowCreateTeamGroupModal}
            isDisabled={!canCreateTeams}
            showTooltip={!canCreateTeams}
            tooltip={`You are not permitted to create ${activeTab === 'teams' ? 'teams' : 'team groups'}.`}
          >
            Create {activeTab === 'teams' ? 'Team' : 'Team Group'}
          </SettingsPageTableActions.TooltipButton>
          <SettingsPageTableActions.SearchInput
            label={`Search ${activeTab === 'teams' ? 'teams' : 'team groups'}`}
            value={this.state.searchValue}
            onChange={(nextSearchValue) => this.setState({ searchValue: nextSearchValue })}
            placeholder={`Search ${activeTab === 'teams' ? 'teams' : 'team groups'}`}
            size="medium"
          />
        </SettingsPageTableActions>
        {activeTab === 'teams' ? (
          <SimpleReactTable
            data={filteredTeams}
            columns={this.getColumns()}
            defaultSortMode={[
              {
                id: 'name',
                desc: false,
              },
            ]}
            onSortedChanged={this.onSortedChanged}
            showPagination={filteredTeams.length > 20}
            cacheId="organizationTeams"
            isLoading={isFetchingTeams}
          />
        ) : (
          <TeamGroupsTable search={searchValue} />
        )}
        {canCreateTeams && (
          <CreateTeamModal
            isOpen={this.state.showCreateTeamModal}
            onHide={this.onHideCreateTeamModal}
          />
        )}
        <EditTeamModal
          isOpen={this.state.teamToEdit !== null}
          onHide={this.onHideEditTeamModal}
          team={this.state.teamToEdit}
        />
        {isTeamGroupsEnabled && (
          <CreateTeamGroupModal
            isOpen={this.state.showCreateTeamGroupModal}
            onHide={this.onHideCreateTeamGroupModal}
          />
        )}
      </AccessGate>
    );
  }
}

/**
 * Container component which will "inject" hook-specific props into the component.
 */
const TeamsIndexContainer = (props: Omit<Props, 'isTeamGroupsEnabled'>) => {
  const isTeamGroupsEnabled = isFeatureEnabled(TEAM_GROUPS);
  const { data: teams, isLoading, error } = useTeams();
  const [query, setQuery] = useQuery<{ tab?: 'teams' | 'teamGroups' }>();

  const setActiveTab = useCallback((tab: 'teams' | 'teamGroups') => {
    //  Always unset the query if team groups are not enabled.
    if (!isTeamGroupsEnabled) {
      setQuery({ tab: undefined });
      return;
    }

    if (!tab || (tab !== 'teams' && tab !== 'teamGroups')) {
      setQuery({ tab: 'teams' });
    } else {
      setQuery({ tab });
    }
  }, [isTeamGroupsEnabled, setQuery]);

  //  Ensure that there is always a tab set.
  useEffect(() => {
    if (!isTeamGroupsEnabled || !query?.tab) {
      setActiveTab('teams');
    }
  }, [isTeamGroupsEnabled, query?.tab, setActiveTab]);

  return (
    <OrganizationTeamsIndex
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      isTeamGroupsEnabled={isTeamGroupsEnabled}
      teams={teams ?? []}
      activeTab={query.tab || 'teams'}
      setActiveTab={setActiveTab}
      isFetchingTeams={isLoading}
      fetchTeamsError={error}
    />
  );
};

export default TeamsIndexContainer;
